import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import b from 'b_';
import { ReduxTeacherStore } from 'client/bundles/lab/typings';
import { TableCell, TableRow, TableRowNumber } from 'platform-components';
import getStudentName from 'platform-components/src/utils/get-student-name';
import {
    isFirstNameAndLastNameExists,
    isValidHumanName,
    hasStudentValidLastNameAndFirstName,
    isStrNullOrEmpty,
} from 'platform-components/src/utils/input-validators';
import React from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { getIsSchoolCodeChanged } from 'common.components/notifications/selectors';
import { StudentDataTableCell } from 'common.components/students/__student-data-table-cell/students__student-data-table-cell';
import { StudentNameTableCell } from 'common.components/students/__student-name-table-cell/students__student-name-table-cell';
import { StudentPassportDataTableCell } from 'common.components/students/__student-passport-data-table-cell/students__student-passport-data-table-cell';
import { Student } from 'common.components/typings/class';

import StudentSettings from '../__table-student-settings/students__table-student-settings';
import * as studentsActions from '../actions';
import {
    getCurrentEditingStudentName,
    getCurrentEditingStudentIndex,
    getEditStudentNameStatus,
    getIsAddingNewStudent,
    getNewStudentEditingStudentName,
    getCurrentPassportChangeStudentIndex,
    getStudentChangePassportStatus,
} from '../selectors';

import './students__student-row.scss';

interface StoreProps {
    currentEditingStudentIndex: number;
    currentPassportChangeStudentIndex: number;
    editedName: string;
    editStudentNameStatus: LOADING_STATUSES;
    isAddingNewStudent: boolean;
    isEditedStudentNameValidForPassport: boolean;
    isSchoolCodeChanged: boolean;
    newStudentEditedName: string | null;
    studentChangePassportStatus: LOADING_STATUSES;
    teamId: number;
}

interface DispatchProps {
    deleteStudentRequest: typeof studentsActions.deleteStudentRequest;
    editStudentNameRequest: typeof studentsActions.editStudentNameRequest;
    migrateStudentToPassport: typeof studentsActions.putUserConvertToPassportRequest;
    saveNewStudentRequest: typeof studentsActions.saveNewStudentRequest;
    setCurrentEditingStudentIndex: typeof studentsActions.setCurrentEditingStudentIndex;
    setCurrentEditingStudentName: typeof studentsActions.setCurrentEditingStudentName;
    setStudentIsMigrating: typeof studentsActions.setStudentValidation;
}

interface OwnProps {
    isArchiveClass: boolean;
    classId: number;
    code: string;
    showPassportAccount: boolean;
    student: Student;
    students: Student[];
    studentsCount: number;
}

interface Props extends OwnProps, StoreProps, DispatchProps {}

export class ClassStudentRow extends React.Component<Props> {
    state = {
        focused: !this.props.editedName,
        shouldShowError: false,
    };

    handleNameChange = () => {
        const { setCurrentEditingStudentIndex, setCurrentEditingStudentName, student } = this.props;
        const { id, firstName, lastName } = student;
        const name = getStudentName(firstName, lastName);

        if (this.isEditButtonDisabled()) {
            return;
        }

        setCurrentEditingStudentName({ id, name });
        setCurrentEditingStudentIndex(id);
        ym('reachGoal', 'teacher_my_students_student_rename');

        this.setState({ focused: true, shouldShowError: false });
    };

    handleInputChange = () => {
        this.setState({ shouldShowError: false });
    };

    getShouldShowError = () => !this.isStudentValid() || this.props.editStudentNameStatus === LOADING_STATUSES.ERROR;

    handleInputBlur = () => {
        this.setState({ focused: false, shouldShowError: this.getShouldShowError() });
    };

    handleEnterKeyDown = () => {
        this.setState({
            focused: false,
            shouldShowError: this.getShouldShowError(),
        });
    };

    handleInputFocus = () => {
        this.setState({ focused: true, shouldShowError: false });
    };

    handleMigrateStudentClick = () => {
        const { migrateStudentToPassport, setStudentIsMigrating, student, classId } = this.props;
        const { id } = student;
        const isStudentValid = hasStudentValidLastNameAndFirstName(student);

        setStudentIsMigrating(id, { isMigrating: true, correct: isStudentValid });

        if (isStudentValid) {
            migrateStudentToPassport(classId, student);
        }
    };

    isStudentValid = () => {
        return this.getStudentErrorMsgId() === null;
    };

    getStudentErrorMsgId = () => {
        const { editedName, newStudentEditedName, isAddingNewStudent, student, isEditedStudentNameValidForPassport } =
            this.props;

        const { no_yandex_account: noYandexAccount } = student;
        const name = newStudentEditedName && isAddingNewStudent ? newStudentEditedName : editedName;

        if (isStrNullOrEmpty(name)) {
            return 'addClass.validation.emptyStudentName';
        }

        if (!isValidHumanName(name)) {
            return 'addClass.validation.incorrectSymbol';
        }

        // При наличии детского паспорта запрещаем иметь пустое имя
        if (noYandexAccount === false && !isEditedStudentNameValidForPassport) {
            return 'addClass.validation.incorrectSurnameOrName';
        }

        return null;
    };

    isEditButtonDisabled = () => {
        const { currentEditingStudentIndex, isAddingNewStudent } = this.props;

        return currentEditingStudentIndex !== null || isAddingNewStudent;
    };

    // eslint-disable-next-line complexity
    render() {
        const {
            isArchiveClass,
            showPassportAccount,
            isSchoolCodeChanged,
            code,
            student,
            classId,
            studentsCount,
            studentChangePassportStatus,
            currentPassportChangeStudentIndex,
        } = this.props;
        const { focused, shouldShowError } = this.state;
        const { id, isDeleting, isNew } = student;

        const isPassportLoading =
            studentChangePassportStatus === LOADING_STATUSES.LOADING && currentPassportChangeStudentIndex === id;

        return (
            <TableRow contentMix={b('students', 'student-row', { new: isNew, deleting: isDeleting })}>
                <TableRowNumber />
                <StudentNameTableCell
                    classId={classId}
                    errorMsgId={this.getStudentErrorMsgId()}
                    focused={focused}
                    isArchiveClass={isArchiveClass}
                    onEnterKeyDown={this.handleEnterKeyDown}
                    onInputBlur={this.handleInputBlur}
                    onInputChange={this.handleInputChange}
                    onInputFocus={this.handleInputFocus}
                    onNameChange={this.handleNameChange}
                    shouldShowError={shouldShowError}
                    student={student}
                    studentsCount={studentsCount}
                />
                {showPassportAccount ? (
                    <StudentPassportDataTableCell isDataLoading={isPassportLoading} student={student} />
                ) : (
                    <StudentDataTableCell code={code} isSchoolCodeChanged={isSchoolCodeChanged} student={student} />
                )}
                <TableCell mix="students__table-student-settings">
                    <StudentSettings
                        classId={classId}
                        isArchiveClass={isArchiveClass}
                        mix={b('students', 'table-student-settings-popup', { short: isArchiveClass })}
                        onMigratePassportRequest={this.handleMigrateStudentClick}
                        onNameChangeRequest={this.handleNameChange}
                        student={student}
                        studentsCount={studentsCount}
                    />
                </TableCell>
            </TableRow>
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: OwnProps) => {
    const { classId, student } = ownProps;
    const editedName = getCurrentEditingStudentName(storeState, { studentId: student.id });
    const newStudentEditedName = getNewStudentEditingStudentName(storeState);
    const isAddingNewStudent = getIsAddingNewStudent(storeState);
    const name = newStudentEditedName && isAddingNewStudent ? newStudentEditedName : editedName;
    const isEditedStudentNameValidForPassport = isFirstNameAndLastNameExists(name);

    return {
        teamId: classId,
        currentEditingStudentIndex: getCurrentEditingStudentIndex(storeState),
        currentPassportChangeStudentIndex: getCurrentPassportChangeStudentIndex(storeState),
        editedName,
        editStudentNameStatus: getEditStudentNameStatus(storeState),
        isAddingNewStudent,
        isEditedStudentNameValidForPassport,
        isSchoolCodeChanged: getIsSchoolCodeChanged(storeState, { classId }),
        newStudentEditedName,
        studentChangePassportStatus: getStudentChangePassportStatus(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
    const {
        deleteStudentRequest,
        setCurrentEditingStudentName,
        editStudentNameRequest,
        saveNewStudentRequest,
        setCurrentEditingStudentIndex,
        putUserConvertToPassportRequest,
        setStudentValidation,
    } = studentsActions;

    return bindActionCreators(
        {
            deleteStudentRequest,
            editStudentNameRequest,
            migrateStudentToPassport: putUserConvertToPassportRequest,
            saveNewStudentRequest,
            setCurrentEditingStudentName,
            setCurrentEditingStudentIndex,
            setStudentIsMigrating: setStudentValidation,
        },
        dispatch
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ClassStudentRow);
